import { useState, useEffect, useCallback } from 'react';
import {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showSyncNotification,
  scheduleScriptReminder,
  cancelScriptReminder,
  getStoredReminders,
  clearExpiredReminders,
  listenForNotificationClicks,
} from '@/lib/pushNotifications';
import { useNavigate } from 'react-router-dom';

interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isEnabled: boolean;
  requestPermission: () => Promise<boolean>;
  notifySyncComplete: (details?: string) => Promise<boolean>;
  notifySyncError: (details?: string) => Promise<boolean>;
  notifySyncConflict: (details?: string) => Promise<boolean>;
  scheduleReminder: (scriptTitle: string, reminderTime: Date, scriptId: string) => number;
  cancelReminder: (timerId: number) => void;
  getReminders: () => Array<{ id: number; scriptId: string; scriptTitle: string; reminderTime: string }>;
}

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    getNotificationPermission()
  );
  const navigate = useNavigate();

  const isSupported = isPushSupported();
  const isEnabled = permission === 'granted';

  useEffect(() => {
    // Clear expired reminders on mount
    clearExpiredReminders();

    // Listen for notification clicks
    const unsubscribe = listenForNotificationClicks((data) => {
      console.log('Notification clicked:', data);
      
      if (data.type === 'reminder' && data.scriptId) {
        // Navigate to the script editor
        navigate(`/editor/${data.scriptId}`);
      } else if (data.type === 'sync-conflict') {
        // Navigate to dashboard
        navigate('/dashboard');
      }
    });

    return unsubscribe;
  }, [navigate]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    const result = await requestNotificationPermission();
    setPermission(result);
    return result === 'granted';
  }, []);

  const notifySyncComplete = useCallback(async (details?: string): Promise<boolean> => {
    return showSyncNotification('complete', details);
  }, []);

  const notifySyncError = useCallback(async (details?: string): Promise<boolean> => {
    return showSyncNotification('error', details);
  }, []);

  const notifySyncConflict = useCallback(async (details?: string): Promise<boolean> => {
    return showSyncNotification('conflict', details);
  }, []);

  const scheduleReminder = useCallback(
    (scriptTitle: string, reminderTime: Date, scriptId: string): number => {
      return scheduleScriptReminder(scriptTitle, reminderTime, scriptId);
    },
    []
  );

  const cancelReminder = useCallback((timerId: number): void => {
    cancelScriptReminder(timerId);
  }, []);

  const getReminders = useCallback(() => {
    return getStoredReminders();
  }, []);

  return {
    isSupported,
    permission,
    isEnabled,
    requestPermission,
    notifySyncComplete,
    notifySyncError,
    notifySyncConflict,
    scheduleReminder,
    cancelReminder,
    getReminders,
  };
};
